import { ExpressionEngine } from '@alter/avatar-core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AvatarCanvas } from './AvatarCanvas';
import { LimiteDeErrores } from './LimiteDeErrores';
import type { AvatarDelEscenario } from './manifiesto';
import { MarcadorPaciente } from './MarcadorPaciente';
import type { ControlAvatar } from '../features/simulacion/orquestador';
import type { ThreeRigAdapter } from './ThreeRigAdapter';
import { useAssetDelEscenario } from './useAvatar';

export interface AvatarStageProps {
  /** `escenario.avatar` de mi-simulacion: el asset se resuelve por id y género. */
  avatar: AvatarDelEscenario;
  /**
   * Se llama con el control del avatar cuando terminó de cargar, y con
   * `null` al desmontarse o si el avatar falla. La expresión llega ya
   * remapeada (`expresion_avatar`); una desconocida cae a `NEUTRAL` en el
   * motor.
   */
  onListo: (control: ControlAvatar | null) => void;
}

/**
 * Contenedor del avatar en la pantalla de sesión (`04_ARQUITECTURA.md`
 * §8.1): resuelve el asset del escenario con el manifiesto, monta el
 * canvas de three.js y le asocia el `ExpressionEngine`. La capa idle viene
 * encendida en el adapter: el avatar tiene vida entre turnos (§6.6).
 *
 * **La sesión no depende del avatar (D-9).** Mientras el `.glb` carga, si
 * el escenario no tiene asset o si el asset falla, se ve el marcador; el
 * avatar aparece cuando llega, sin interrumpir la entrevista.
 */
export function AvatarStage({ avatar, onListo }: AvatarStageProps) {
  const resuelto = useAssetDelEscenario(avatar);
  const url = resuelto.estado === 'resuelto' ? resuelto.asset?.url : undefined;
  const [listo, setListo] = useState(false);
  const [fallo, setFallo] = useState(false);

  const onListoRef = useRef(onListo);
  onListoRef.current = onListo;
  const engineRef = useRef<ExpressionEngine | null>(null);
  const adapterRef = useRef<ThreeRigAdapter | null>(null);

  const handleAdapterReady = useCallback((adapter: ThreeRigAdapter) => {
    const engine = new ExpressionEngine(adapter);
    engineRef.current = engine;
    adapterRef.current = adapter;
    setListo(true);
    onListoRef.current(control(engine, adapter));
  }, []);

  const handleFallo = useCallback(() => {
    engineRef.current = null;
    adapterRef.current = null;
    setFallo(true);
    onListoRef.current(null);
  }, []);

  // El fallo es del asset, no de la pantalla: si cambia la URL hay que
  // volver a intentar. El límite se rearma solo con su prop `url`; este
  // estado es el otro candado y también tiene que abrirse.
  useEffect(() => {
    setFallo(false);
    setListo(false);
  }, [url]);

  // El adapter puede estar listo antes del desmontaje simulado de
  // StrictMode (el .glb está precargado): el cleanup avisa `null` y el
  // segundo montaje tiene que volver a registrar el motor, o el avatar
  // queda desenganchado y las expresiones no se ven. Pasó en la prueba
  // contra MVP del 2026-09-19.
  useEffect(() => {
    const engine = engineRef.current;
    const adapter = adapterRef.current;
    if (engine && adapter) onListoRef.current(control(engine, adapter));
    return () => onListoRef.current(null);
  }, []);

  const cargando = resuelto.estado === 'resolviendo' || (url !== undefined && !listo && !fallo);

  return (
    <div className="absolute inset-0">
      {url !== undefined && !fallo && (
        <LimiteDeErrores url={url} avatarId={avatar.id} onError={handleFallo}>
          <AvatarCanvas url={url} onAdapterReady={handleAdapterReady} />
        </LimiteDeErrores>
      )}
      {!listo || fallo ? <MarcadorPaciente cargando={cargando} /> : null}
    </div>
  );
}

function control(engine: ExpressionEngine, adapter: ThreeRigAdapter): ControlAvatar {
  return {
    aplicarExpresion: (expresion, duracionMs) => {
      engine.apply(expresion, duracionMs !== undefined ? { durationMs: duracionMs } : undefined);
    },
    hablar: (envolvente, desfaseMs) => {
      adapter.hablar(envolvente, desfaseMs);
    },
    dejarDeHablar: () => {
      adapter.dejarDeHablar();
    },
    pensar: (activo) => {
      adapter.pensar(activo);
    },
    inspirar: (prerollMs) => {
      adapter.inspirar(prerollMs);
    },
  };
}
